(async () => {
  if (!customElements.get("ugc-slider-swiper")) {
    const { default: Swiper, Autoplay, Navigation } = await import("swiper");

    Swiper.use([Autoplay, Navigation]);

    class UGCSwiper extends HTMLElement {
      constructor() {
        super();

        this.swiperElement = this.querySelector("[data-swiper]");
        this.swiperOptions = {
          threshold: 10,
        };

        // Check if we have extra options on the HTML
        if (this && this.dataset && this.dataset.options) {
          try {
            const jsonOptions = JSON.parse(this.dataset.options);
            if (jsonOptions) {
              this.swiperOptions = {
                ...this.swiperOptions,
                ...jsonOptions,
              };
            }
          } catch (e) {
            // Silently handle JSON parsing errors
          }
        }

        if (this.swiperElement) {
          this.swiperInstance = new Swiper(
            this.swiperElement,
            this.swiperOptions
          );
          this.initVideoControl();

          // Listen for slide changes to debug visibility
          this.swiperInstance.on("slideChange", () => {
            // Force a visibility check after slide change
            setTimeout(() => {
              this.debugCurrentSlideVisibility();
            }, 100);
          });
        }

        // re-init slider when Theme Editor is active
        if (Shopify.designMode && this.swiperInstance) {
          window.addEventListener("shopify:section:load", () => {
            this.swiperInstance.update();
          });
        }
      }

      initVideoControl() {
        // First, ensure the first video plays if it's visible
        this.ensureFirstVideoPlays();
        // Then set up intersection observer for all slides
        this.setupVideoIntersectionObserver();
      }

      setupVideoIntersectionObserver() {
        if (!("IntersectionObserver" in window)) {
          return;
        }

        const observerOptions = {
          root: this.swiperElement,
          threshold: [0, 0.5, 1.0], // Multiple thresholds for better detection
          rootMargin: "0px",
        };

        this.videoObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const video = entry.target.querySelector("video");
            if (!video) return;

            if (entry.isIntersecting && entry.intersectionRatio >= 0.9) {
              // Wait for lazy video to be ready before playing
              this.waitForVideoReady(video, () => {
                this.playVideo(video);
              });
            } else {
              this.pauseVideo(video);
            }
          });
        }, observerOptions);

        // Find all real slides with videos (excluding the first slide which we handle separately)
        const slides = this.swiperElement.querySelectorAll(".swiper-slide");

        slides.forEach((slide, index) => {
          const video = slide.querySelector("video");
          // Only observe non-duplicate slides with videos, but skip the first real slide
          if (
            video &&
            !slide.classList.contains("swiper-slide-duplicate") &&
            !slide.classList.contains("swiper-slide-duplicate-next") &&
            !slide.classList.contains("swiper-slide-duplicate-prev")
          ) {
            const slideIndex = slide.getAttribute("data-swiper-slide-index");
            if (slideIndex === "0") {
              // First slide handled separately in ensureFirstVideoPlays
            } else {
              this.videoObserver.observe(slide);
            }
          }
        });

        // Set up a visibility check interval for more responsive video control
        this.setupVisibilityCheckInterval();
      }

      setupVisibilityCheckInterval() {
        // Check video visibility every 100ms for more responsive control
        this.visibilityInterval = setInterval(() => {
          const slides = this.swiperElement.querySelectorAll(".swiper-slide");

          slides.forEach((slide) => {
            if (slide.classList.contains("swiper-slide-duplicate")) return;

            const video = slide.querySelector("video");
            if (!video) return;

            const slideIndex = slide.getAttribute("data-swiper-slide-index");
            const isFullyVisible = this.isSlideFullyVisible(slide);

            if (isFullyVisible && video.paused) {
              // Video should be playing but is paused
              this.waitForVideoReady(video, () => {
                this.playVideo(video);
              });
            } else if (!isFullyVisible && !video.paused) {
              // Video should be paused but is playing
              this.pauseVideo(video);
            }
          });

          // Also check the first slide specifically for loop scenarios
          this.checkFirstSlideOnLoop();
        }, 100);
      }

      checkFirstSlideOnLoop() {
        const allSlides = this.swiperElement.querySelectorAll(".swiper-slide");

        for (let i = 0; i < allSlides.length; i++) {
          const slide = allSlides[i];
          const slideIndex = slide.getAttribute("data-swiper-slide-index");

          if (slideIndex !== "0") continue;

          const video = slide.querySelector("video");
          if (!video) continue;

          const isVisible = this.isSlideVisible(slide);
          const isFullyVisible = this.isSlideFullyVisible(slide);

          if (isVisible && isFullyVisible && video.paused) {
            this.waitForVideoReady(video, () => {
              this.playVideo(video);
            });
            break;
          }
        }
      }

      ensureFirstVideoPlays() {
        // Check if the first slide has a video and is visible
        // Process all slides including duplicates since Swiper might show a duplicate as first
        const allSlides = this.swiperElement.querySelectorAll(".swiper-slide");
        let firstSlide = null;

        // Look for the first slide with index 0 (first content)
        for (let i = 0; i < allSlides.length; i++) {
          const slide = allSlides[i];
          const slideIndex = slide.getAttribute("data-swiper-slide-index");

          if (slideIndex === "0") {
            firstSlide = slide;
            break;
          }
        }

        if (firstSlide) {
          const video = firstSlide.querySelector("video");
          if (video) {
            // Wait for the lazy video to be loaded before playing
            this.waitForVideoReady(video, () => {
              // Check if the first slide is actually visible in the viewport
              // instead of relying on Swiper's activeIndex which might not be 0 initially
              const isFirstSlideVisible = this.isSlideVisible(firstSlide);

              if (isFirstSlideVisible) {
                // Play the first video directly since it's already visible
                this.playVideo(video);
              } else {
                // If first slide isn't visible, observe it with the intersection observer
                // so it will play when it becomes visible
                if (this.videoObserver) {
                  this.videoObserver.observe(firstSlide);
                }
              }
            });
          }
        }
      }

      isSlideVisible(slide) {
        if (!slide) return false;

        const rect = slide.getBoundingClientRect();
        const containerRect = this.swiperElement.getBoundingClientRect();

        // Check if the slide is visible within the container
        // A slide is visible if it's within the container bounds
        const isVisible =
          rect.left < containerRect.right &&
          rect.right > containerRect.left &&
          rect.width > 0 &&
          rect.height > 0;

        return isVisible;
      }

      isSlideFullyVisible(slide) {
        if (!slide) return false;

        const rect = slide.getBoundingClientRect();
        const containerRect = this.swiperElement.getBoundingClientRect();

        // Add small tolerance for floating-point precision issues
        const tolerance = 1; // 1 pixel tolerance

        // Check if the slide is fully visible within the container
        // A slide is fully visible if it's completely within the container bounds
        // For horizontal slider: left edge >= container left AND right edge <= container right
        const isFullyVisible =
          rect.left + tolerance >= containerRect.left &&
          rect.right - tolerance <= containerRect.right &&
          rect.width > 0 &&
          rect.height > 0;

        return isFullyVisible;
      }

      debugCurrentSlideVisibility() {
        if (!this.swiperInstance) return;

        const activeIndex = this.swiperInstance.activeIndex;
        const activeSlide = this.swiperElement.querySelector(
          `[data-swiper-slide-index="${activeIndex}"]`
        );

        if (activeSlide) {
          const video = activeSlide.querySelector("video");
          if (video) {
            const isFullyVisible = this.isSlideFullyVisible(activeSlide);
            // Video visibility logic handled in setupVisibilityCheckInterval
          }
        }
      }

      waitForVideoReady(video, callback) {
        // Check if video is already loaded (not lazy)
        if (!video.classList.contains("lazy-video")) {
          callback();
          return;
        }

        // Wait for lazy video to load
        const checkVideoReady = () => {
          if (!video.classList.contains("lazy-video")) {
            // Video is loaded, execute callback
            callback();
          } else {
            // Check again in a short delay
            setTimeout(checkVideoReady, 100);
          }
        };

        // Start checking
        checkVideoReady();
      }

      playVideo(video) {
        if (video && video.paused) {
          // Ensure video is muted and has proper attributes for autoplay
          video.muted = true;
          video.playsInline = true;

          video.play().catch((e) => {
            // Handle autoplay restrictions silently
          });
        }
      }

      pauseVideo(video) {
        if (video && !video.paused) {
          video.pause();
        }
      }

      disconnectedCallback() {
        // Clean up intersection observer when component is removed
        if (this.videoObserver) {
          this.videoObserver.disconnect();
        }
        if (this.visibilityInterval) {
          clearInterval(this.visibilityInterval);
        }
      }
    }

    window.UGCSwiper = UGCSwiper;
    customElements.define("ugc-slider-swiper", UGCSwiper);
  }
})();
